import { useStore } from '@/store';
import { useNotify, useRecentFiles } from '@/hooks';
import { useFileToTextParser } from '@/api';
import { useEffect, useState } from 'react';
import { ACCEPTED_FILE_EXTENSIONS, ACCEPTED_FILE_MIME_TYPES } from '@/config';
import { t } from 'i18next';
import { resetProgress, usePlugins } from '@morph-mapper/plugins';
import { recomputeDependencies } from '@/utils';

export const useFileSelectEditor = () => {
  /* Global state */
  const [setStoreFile, setTextContent, variant, templateId] = useStore(
    ({ config: c }) => [c.setFile, c.setTextContent, c.getVariant(), c.getId()],
  );
  const entries = useStore(({ entries: e }) => e.entries);
  const setRebootstrapInProgress = useStore(
    (s) => s.ai.setRebootstrapInProgress,
  );
  const completeRebootstrap = useStore((s) => s.ai.completeRebootstrap);
  /* Plugins */
  const { bootstrap } = usePlugins();
  /* Local state */
  const [file, setFile] = useState<File | undefined>();
  const [parseTrigger, setParseTrigger] = useState(0);
  const [notification, setNotification] = useState<string | undefined>(
    undefined,
  );
  /* Hooks */
  const { close, loading, error: notifyError } = useNotify();
  const {
    recentFiles,
    addRecentFile,
    loadRecentFile,
    downloadRecentFile,
    removeRecentFile,
  } = useRecentFiles(templateId);
  /* Queries */
  const { data, error, isError, dataUpdatedAt } = useFileToTextParser(
    file,
    parseTrigger,
  );

  // Update recent files when a new file is selected
  const updateRecentFiles = async (file: File) => {
    if (data === undefined) {
      return;
    }

    await addRecentFile(file);
  };

  const handleRecentFileSelect = async (fileId: string) => {
    const selectedFileResult = await loadRecentFile(fileId);
    if (selectedFileResult.isErr()) {
      return;
    }

    setFile(selectedFileResult.value);
    setParseTrigger((prev) => prev + 1);
  };

  const handleRecentFileDownload = async (fileId: string) => {
    await downloadRecentFile(fileId);
  };

  const handleRecentFileDelete = async (fileId: string) => {
    await removeRecentFile(fileId);
  };

  /**
   * Effects
   */
  useEffect(() => {
    if (file === undefined) {
      return;
    }

    const id = loading(t('message.loading.file'));
    setNotification(id);
  }, [file]);

  useEffect(() => {
    if (data === undefined) {
      return;
    }
    if (dataUpdatedAt === 0) {
      return;
    }

    setTextContent(data);
    setRebootstrapInProgress(true);
    resetProgress();

    const localEntries = JSON.parse(JSON.stringify(entries));
    void (async () => {
      try {
        await bootstrap({
          entries: localEntries,
          textContent: data,
          recomputeDependencies,
          force: true,
        });
        completeRebootstrap();
      } catch (err: unknown) {
        console.error(
          '[FileSelect] Failed to re-bootstrap plugins after file change:',
          err,
        );
        setRebootstrapInProgress(false);
      }
    })();
  }, [data, dataUpdatedAt, setRebootstrapInProgress, completeRebootstrap]);

  useEffect(() => {
    if (!isError) {
      return;
    }

    if (notification) {
      close(notification);
      setNotification(undefined);
    }

    notifyError(t('message.error.fileParseFailed'));
  }, [isError, error]);

  useEffect(() => {
    if (notification === undefined) {
      return;
    }
    if (data === undefined) {
      return;
    }
    if (dataUpdatedAt === 0) {
      return;
    }

    close(notification);
    setNotification(undefined);

    // Persist selected file globally and update recents
    if (file) {
      setStoreFile(file);
      updateRecentFiles(file);
    }
  }, [data, dataUpdatedAt, notification]);

  const getAcceptedFileExtensionsForInput = () => {
    // Native HTML file input accepts both MIME types and file extensions
    // Include both for better browser compatibility
    if (!variant) {
      return '';
    }
    const mimeTypes = ACCEPTED_FILE_MIME_TYPES[variant] || [];
    const extensions = ACCEPTED_FILE_EXTENSIONS[variant] || [];
    return [...mimeTypes, ...extensions].join(',');
  };

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setParseTrigger((prev) => prev + 1);
  };

  return {
    handleFileSelect,
    handleRecentFileSelect,
    recentFiles,
    handleRecentFileDownload,
    handleRecentFileDelete,
    getAcceptedFileExtensionsForInput,
  };
};
